import React from "react";
import * as ReactDOMClient from "react-dom/client";
import App from "./App.tsx";
import AppShell from "./components/layout/AppShell";
import "./index.css"; // Default Vite CSS, can be kept or removed if global.css covers all needs
import "./styles/global.css"; // Global tokens
import "./styles/isolation.css"; // Scoped isolation under widget/fullpage roots
import { mountInShadowRoot } from "./utils/shadowMount";
// import * as Sentry from "@sentry/react"; // Added Sentry import
import { logError } from "./utils/logging"; // Import logging utility
import {
  setAnalyticsModeResolver,
  AnalyticsMode,
} from "./services/analyticsService";
import { useUIStore } from "@store";
import { initDeepLinkHandler } from "./utils/deepLinkHandler";
import ErrorBoundary from "./components/common/ErrorBoundary";

// Robust createRoot retrieval to handle various bundler/environment ESM/CJS mismatches
type CreateRootFn = typeof ReactDOMClient.createRoot;
type ReactDOMClientLike = typeof ReactDOMClient & {
  default?: { createRoot?: CreateRootFn } | CreateRootFn;
};
const rdc = ReactDOMClient as ReactDOMClientLike;
const maybeDefault = rdc.default;
const createRoot: CreateRootFn | undefined =
  rdc.createRoot ||
  (typeof maybeDefault === "object" ? maybeDefault?.createRoot : undefined) ||
  (typeof maybeDefault === "function" ? maybeDefault : undefined);

if (!createRoot || typeof createRoot !== "function") {
  console.error(
    "Smartie AI: Failed to resolve createRoot from react-dom/client.",
    ReactDOMClient,
  );
}

// Initialize Sentry
// IMPORTANT: Replace with your actual DSN and configure release/environment properly.
/*
if (import.meta.env.PROD) {
  // Only initialize Sentry in production builds
  Sentry.init({
    dsn: "https://4f7df707eb7bac1c427d866070f43e6c@o4509372859285504.ingest.de.sentry.io/4509372860792912",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Additional Replay configuration, if needed:
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% of transactions for performance monitoring. Adjust as needed.
    // Session Replay
    replaysSessionSampleRate: 0.05, // This sets the sample rate at 5%. Adjust as needed.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, sample the session when an error occurs.

    environment: import.meta.env.MODE, // e.g., 'production', 'development'
    release: "smartie-frontend@" + __APP_VERSION__, // Using __APP_VERSION__ defined via Vite
    // For release, ensure your Vite config defines __APP_VERSION__ (e.g., from package.json version)
    // Example in vite.config.ts: define: { '__APP_VERSION__': JSON.stringify(process.env.npm_package_version) }
    denyUrls: [
      /\/wp-content\/plugins\/buddyboss-platform\//,
    ],
  });
}
*/

// Dev helper: allow forcing mount mode via URL, e.g. ?mode=fullpage or ?mode=widget
const params = new URLSearchParams(window.location.search);
const forcedMode = params.get("mode"); // "fullpage" | "widget" | null
const forceNoShadow = params.get("smartie_no_shadow"); // "true" to disable shadow DOM

let widgetRoot = document.getElementById("smartie-chat-widget-root");
let fullpageRoot = document.getElementById("smartie-fullpage-root");

// If a mode is forced but the corresponding root is missing (common in Vite dev), create it
if (!fullpageRoot && forcedMode === "fullpage") {
  fullpageRoot = document.createElement("div");
  fullpageRoot.id = "smartie-fullpage-root";
  document.body.appendChild(fullpageRoot);
}

if (!widgetRoot && forcedMode === "widget") {
  widgetRoot = document.createElement("div");
  widgetRoot.id = "smartie-chat-widget-root";
  document.body.appendChild(widgetRoot);
}

if (fullpageRoot) {
  // In fullpage mode, mode is always 'fullpage'
  setAnalyticsModeResolver((): AnalyticsMode => "fullpage");
  // In development OR if shadow is explicitly disabled, mount directly
  if (import.meta.env.DEV || forceNoShadow) {
    createRoot(fullpageRoot).render(
      <React.StrictMode>
        <ErrorBoundary>
          <AppShell />
        </ErrorBoundary>
      </React.StrictMode>,
    );
  } else {
    // In production (WordPress), mount inside Shadow DOM for style isolation and clone plugin styles
    mountInShadowRoot(fullpageRoot, (container) => {
      createRoot(container).render(
        <React.StrictMode>
          <ErrorBoundary>
            <AppShell />
          </ErrorBoundary>
        </React.StrictMode>,
      );
    });
  }
} else if (widgetRoot) {
  // In widget mode, compute mode from current UI state when events fire
  setAnalyticsModeResolver((): AnalyticsMode | undefined => {
    const viewMode = useUIStore.getState().chatViewMode;
    if (viewMode === "fullscreen") return "expanded";
    if (viewMode === "sidebar") return "narrow";
    // collapsed -> returns undefined to avoid misleading mode; most events won't fire while collapsed
    return undefined;
  });
  createRoot(widgetRoot).render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>,
  );
} else {
  logError(
    "Failed to find Smartie root elements. Expected #smartie-fullpage-root or #smartie-chat-widget-root.",
  );
}

// Initialize deep link handler for data-smartie-prompt attributes
// This allows external links to trigger predefined prompts
initDeepLinkHandler();

